/**
 * Internal Action Constants
 *
 * Defines balance constants for Internal party management actions.
 * Internal actions help players maintain candidate loyalty, address grievances,
 * and manage their party's internal stability.
 *
 * @module constants/internal
 */

import type { CandidateRole } from '@/types'

// ============================================================================
// Types
// ============================================================================

export type InternalSubtype = 'loyalty_maintenance' | 'address_grievance' | 'promote_candidate'

/**
 * Configuration for an internal action subtype.
 */
export interface InternalSubtypeConfig {
  key: InternalSubtype
  label: string
  description: string
  icon: string
  detailedDescription: string
  hasIntensitySlider: boolean
  apCost: number | [number, number] // Fixed cost or [min, max] range
}

/**
 * Configuration for a loyalty maintenance theme.
 */
export interface MaintenanceThemeConfig {
  key: string
  label: string
  description: string
}

/**
 * Configuration for a grievance approach.
 */
export interface GrievanceApproachConfig {
  key: string
  label: string
  description: string
}

// ============================================================================
// Internal Subtype Configurations
// ============================================================================

export const INTERNAL_SUBTYPES: Record<InternalSubtype, InternalSubtypeConfig> = {
  loyalty_maintenance: {
    key: 'loyalty_maintenance',
    label: 'Loyalty Maintenance',
    description: 'Hold party conferences and team-building activities to boost morale across all candidates',
    icon: '🤝',
    detailedDescription:
      'Provides a small loyalty boost to ALL candidates in the party. Good baseline maintenance action to prevent gradual loyalty decay from becoming problematic.',
    hasIntensitySlider: true,
    apCost: [1, 2],
  },
  address_grievance: {
    key: 'address_grievance',
    label: 'Address Grievance',
    description: 'Meet privately with a dissatisfied candidate to address their concerns',
    icon: '💬',
    detailedDescription:
      'Provides a significant loyalty boost to ONE candidate. More efficient than loyalty maintenance for critical situations where a specific candidate needs attention.',
    hasIntensitySlider: true,
    apCost: [1, 2],
  },
  promote_candidate: {
    key: 'promote_candidate',
    label: 'Promote Candidate',
    description: 'Assign a candidate to a new role within the party',
    icon: '📈',
    detailedDescription:
      'Promotions boost loyalty significantly. Demotions or removals reduce loyalty. Creates strategic tradeoffs between candidate happiness and optimal placement.',
    hasIntensitySlider: false,
    apCost: 1,
  },
} as const

// ============================================================================
// Loyalty Maintenance Themes
// ============================================================================

export const MAINTENANCE_THEMES: MaintenanceThemeConfig[] = [
  {
    key: 'unity_rally',
    label: 'Party Unity Rally',
    description: 'Emphasize shared purpose and collective strength',
  },
  {
    key: 'appreciation_event',
    label: 'Leadership Appreciation Event',
    description: 'Recognize individual contributions and hard work',
  },
  {
    key: 'policy_conference',
    label: 'Policy Vision Conference',
    description: 'Reinforce ideological commitment and policy goals',
  },
  {
    key: 'grassroots_tour',
    label: 'Grassroots Thank You Tour',
    description: 'Connect with local volunteers and party workers',
  },
] as const

// ============================================================================
// Grievance Approaches
// ============================================================================

export const GRIEVANCE_APPROACHES: GrievanceApproachConfig[] = [
  {
    key: 'listen_validate',
    label: 'Listen and Validate',
    description: 'Show empathy and acknowledge their concerns',
  },
  {
    key: 'promise_opportunities',
    label: 'Promise Future Opportunities',
    description: 'Offer prospects for advancement or better roles',
  },
  {
    key: 'ideological_reassurance',
    label: 'Ideological Reassurance',
    description: 'Reaffirm shared values and party direction',
  },
  {
    key: 'material_incentives',
    label: 'Material Incentives',
    description: 'Offer tangible benefits or improved conditions',
  },
] as const

// ============================================================================
// Role Display Configuration
// ============================================================================

export interface RoleConfig {
  label: string
  description: string
  isUnique: boolean // Only one candidate can have this role
}

export const ROLE_CONFIGS: Record<CandidateRole, RoleConfig> = {
  none: {
    label: 'No Role',
    description: 'Not assigned to any specific role',
    isUnique: false,
  },
  regional_candidate: {
    label: 'Regional Candidate',
    description: 'Running in a specific district',
    isUnique: false, // One per district, but multiple can exist
  },
  list_candidate: {
    label: 'List Candidate',
    description: 'On party list for proportional allocation',
    isUnique: false,
  },
  party_leader: {
    label: 'Party Leader',
    description: 'Leader of the party',
    isUnique: true,
  },
  minister: {
    label: 'Minister',
    description: 'Serving in cabinet',
    isUnique: false,
  },
  chair: {
    label: 'Committee Chair',
    description: 'Committee chair or similar role',
    isUnique: false,
  },
} as const

// Order for role selection dropdown
export const ROLE_ORDER: CandidateRole[] = [
  'none',
  'regional_candidate',
  'list_candidate',
  'party_leader',
  'chair',
]

// ============================================================================
// Internal Effect Constants
// ============================================================================

export const INTERNAL_EFFECTS = {
  // -------------------------------------------------------------------------
  // Loyalty Maintenance Effects
  // -------------------------------------------------------------------------

  /**
   * Base loyalty boost for loyalty maintenance.
   * @tuning Increase for more impactful maintenance
   */
  MAINTENANCE_BASE_LOYALTY: 2,

  /**
   * Additional loyalty per AP spent for maintenance.
   * At 1 AP: +4 loyalty to everyone
   * At 2 AP: +6 loyalty to everyone
   * @tuning Increase to reward higher AP investment
   */
  MAINTENANCE_LOYALTY_PER_AP: 2,

  // -------------------------------------------------------------------------
  // Address Grievance Effects
  // -------------------------------------------------------------------------

  /**
   * Base loyalty boost for addressing grievance.
   * @tuning Increase for more impactful grievance resolution
   */
  GRIEVANCE_BASE_LOYALTY: 8,

  /**
   * Additional loyalty per AP spent for grievance.
   * At 1 AP: +14 loyalty to one candidate
   * At 2 AP: +20 loyalty to one candidate
   * @tuning Increase to reward higher AP investment
   */
  GRIEVANCE_LOYALTY_PER_AP: 6,

  // -------------------------------------------------------------------------
  // Promote Candidate Effects
  // -------------------------------------------------------------------------

  /**
   * Loyalty boost for promotion from no role to any role.
   * @tuning Increase to make promotions more rewarding
   */
  PROMOTION_FROM_NONE_LOYALTY: 10,

  /**
   * Loyalty boost for promotion to party leader.
   * @tuning Increase to make leadership promotion more valuable
   */
  PROMOTION_TO_LEADER_LOYALTY: 15,

  /**
   * Loyalty change for lateral moves (same tier roles).
   * @tuning Adjust for lateral move impact
   */
  LATERAL_MOVE_LOYALTY: 2,

  /**
   * Loyalty penalty for demotion to no role.
   * @tuning Decrease (more negative) to make demotions more punishing
   */
  DEMOTION_TO_NONE_LOYALTY: -15,

  /**
   * Loyalty penalty for demotion from party leader.
   * @tuning Decrease (more negative) to make leader removal more risky
   */
  DEMOTION_FROM_LEADER_LOYALTY: -20,

  /**
   * Loyalty penalty for being replaced as regional candidate.
   * @tuning Decrease (more negative) to make replacements more consequential
   */
  REPLACEMENT_LOYALTY: -12,

  // -------------------------------------------------------------------------
  // Loyalty Thresholds for UI
  // -------------------------------------------------------------------------

  /**
   * Loyalty level considered critical (red).
   */
  LOYALTY_CRITICAL: 30,

  /**
   * Loyalty level considered low (orange).
   */
  LOYALTY_LOW: 50,

  /**
   * Loyalty level considered moderate (yellow).
   */
  LOYALTY_MODERATE: 70,

  /**
   * Loyalty level considered healthy (green).
   */
  LOYALTY_HEALTHY: 100,

  /**
   * Loyalty threshold for warning icon display.
   */
  LOYALTY_WARNING_THRESHOLD: 40,

  // -------------------------------------------------------------------------
  // Loyalty Maintenance Modifiers
  // -------------------------------------------------------------------------

  /**
   * Bonus loyalty for parties with positive momentum (>10).
   * @tuning Increase to reward momentum more
   */
  MAINTENANCE_POSITIVE_MOMENTUM_BONUS: 1,

  /**
   * Bonus loyalty for recent election win (within 2 ticks).
   * @tuning Increase to reward recent wins more
   */
  MAINTENANCE_RECENT_WIN_BONUS: 2,

  /**
   * Ticks window for "recent" election win.
   */
  MAINTENANCE_RECENT_WIN_TICKS: 2,

  /**
   * Penalty for having active scandals.
   * @tuning Decrease (more negative) to make scandals hurt more
   */
  MAINTENANCE_SCANDAL_PENALTY: 1,

  /**
   * Bonus for crisis intervention (low average loyalty).
   * @tuning Increase to make crisis intervention more impactful
   */
  MAINTENANCE_CRISIS_BONUS: 2,

  // -------------------------------------------------------------------------
  // Address Grievance Modifiers
  // -------------------------------------------------------------------------

  /**
   * Bonus for critically low loyalty candidates (<20).
   * @tuning Increase to make desperate interventions more effective
   */
  GRIEVANCE_CRITICAL_LOYALTY_BONUS: 5,

  /**
   * Threshold for critical loyalty bonus.
   */
  GRIEVANCE_CRITICAL_LOYALTY_THRESHOLD: 20,

  /**
   * Penalty for high ambition candidates (>70 ambition).
   * @tuning Increase to make ambitious candidates harder to satisfy
   */
  GRIEVANCE_HIGH_AMBITION_PENALTY: 3,

  /**
   * Threshold for high ambition penalty.
   */
  GRIEVANCE_HIGH_AMBITION_THRESHOLD: 70,

  /**
   * Bonus for ideologically aligned candidates (<15 distance).
   * @tuning Increase to reward ideology alignment
   */
  GRIEVANCE_ALIGNED_IDEOLOGY_BONUS: 3,

  /**
   * Distance threshold for ideology alignment bonus.
   */
  GRIEVANCE_ALIGNED_IDEOLOGY_THRESHOLD: 15,

  /**
   * Penalty for ideologically divergent candidates (>40 distance).
   * @tuning Increase to punish ideology divergence more
   */
  GRIEVANCE_DIVERGENT_IDEOLOGY_PENALTY: 5,

  /**
   * Distance threshold for ideology divergence penalty.
   */
  GRIEVANCE_DIVERGENT_IDEOLOGY_THRESHOLD: 40,

  /**
   * Bonus for addressing career concerns (no role + ambitious).
   * @tuning Increase to reward addressing specific grievances
   */
  GRIEVANCE_CAREER_CONCERNS_BONUS: 2,

  // -------------------------------------------------------------------------
  // AP Constants
  // -------------------------------------------------------------------------

  /**
   * Minimum AP cost for intensity-based internal actions.
   */
  MIN_AP: 1,

  /**
   * Maximum AP cost for intensity-based internal actions.
   */
  MAX_AP: 2,

  /**
   * Fixed AP cost for promote candidate action.
   */
  PROMOTE_AP_COST: 1,

  // -------------------------------------------------------------------------
  // Loyalty Stat Limits
  // -------------------------------------------------------------------------

  /**
   * Minimum loyalty value.
   */
  LOYALTY_MIN: 0,

  /**
   * Maximum loyalty value.
   */
  LOYALTY_MAX: 100,
} as const

// ============================================================================
// Role Tier System (for calculating promotion/demotion)
// ============================================================================

/**
 * Role tiers for calculating promotion/demotion effects.
 * Higher tier = more prestigious role.
 */
export const ROLE_TIERS: Record<CandidateRole, number> = {
  none: 0,
  list_candidate: 1,
  regional_candidate: 1,
  chair: 2,
  party_leader: 3,
  minister: 3,
} as const

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the configuration for an internal action subtype.
 */
export function getInternalSubtypeConfig(subtype: InternalSubtype): InternalSubtypeConfig {
  return INTERNAL_SUBTYPES[subtype]
}

/**
 * Get the configuration for a role.
 */
export function getRoleConfig(role: CandidateRole): RoleConfig {
  return ROLE_CONFIGS[role]
}

/**
 * Calculate estimated loyalty boost for loyalty maintenance.
 */
export function calculateMaintenanceEffects(intensity: number): {
  loyaltyBoost: number
} {
  const loyaltyBoost =
    INTERNAL_EFFECTS.MAINTENANCE_BASE_LOYALTY +
    intensity * INTERNAL_EFFECTS.MAINTENANCE_LOYALTY_PER_AP
  return { loyaltyBoost }
}

/**
 * Calculate estimated loyalty boost for addressing grievance.
 */
export function calculateGrievanceEffects(intensity: number): {
  loyaltyBoost: number
} {
  const loyaltyBoost =
    INTERNAL_EFFECTS.GRIEVANCE_BASE_LOYALTY +
    intensity * INTERNAL_EFFECTS.GRIEVANCE_LOYALTY_PER_AP
  return { loyaltyBoost }
}

/**
 * Determine if a role change is a promotion, demotion, or lateral move.
 */
export function getRoleChangeType(
  currentRole: CandidateRole,
  newRole: CandidateRole
): 'promotion' | 'demotion' | 'lateral' | 'same' {
  if (currentRole === newRole) return 'same'

  const currentTier = ROLE_TIERS[currentRole]
  const newTier = ROLE_TIERS[newRole]

  if (newTier > currentTier) return 'promotion'
  if (newTier < currentTier) return 'demotion'
  return 'lateral'
}

/**
 * Calculate estimated loyalty change for a role change.
 */
export function calculatePromotionEffects(
  currentRole: CandidateRole,
  newRole: CandidateRole
): {
  loyaltyChange: number
  changeType: 'promotion' | 'demotion' | 'lateral' | 'same'
  description: string
} {
  const changeType = getRoleChangeType(currentRole, newRole)

  switch (changeType) {
    case 'same':
      return {
        loyaltyChange: 0,
        changeType,
        description: 'No change',
      }

    case 'promotion': {
      // Special case for promotion to party leader
      if (newRole === 'party_leader') {
        return {
          loyaltyChange: INTERNAL_EFFECTS.PROMOTION_TO_LEADER_LOYALTY,
          changeType,
          description: `+${INTERNAL_EFFECTS.PROMOTION_TO_LEADER_LOYALTY} loyalty from leadership promotion`,
        }
      }
      // Promotion from none to any role
      if (currentRole === 'none') {
        return {
          loyaltyChange: INTERNAL_EFFECTS.PROMOTION_FROM_NONE_LOYALTY,
          changeType,
          description: `+${INTERNAL_EFFECTS.PROMOTION_FROM_NONE_LOYALTY} loyalty from role assignment`,
        }
      }
      // Other promotions (e.g., list_candidate -> chair)
      return {
        loyaltyChange: INTERNAL_EFFECTS.PROMOTION_FROM_NONE_LOYALTY,
        changeType,
        description: `+${INTERNAL_EFFECTS.PROMOTION_FROM_NONE_LOYALTY} loyalty from promotion`,
      }
    }

    case 'demotion': {
      // Special case for demotion from party leader
      if (currentRole === 'party_leader') {
        return {
          loyaltyChange: INTERNAL_EFFECTS.DEMOTION_FROM_LEADER_LOYALTY,
          changeType,
          description: `${INTERNAL_EFFECTS.DEMOTION_FROM_LEADER_LOYALTY} loyalty from leadership removal`,
        }
      }
      // Demotion to none
      if (newRole === 'none') {
        return {
          loyaltyChange: INTERNAL_EFFECTS.DEMOTION_TO_NONE_LOYALTY,
          changeType,
          description: `${INTERNAL_EFFECTS.DEMOTION_TO_NONE_LOYALTY} loyalty from role removal`,
        }
      }
      // Other demotions
      return {
        loyaltyChange: INTERNAL_EFFECTS.DEMOTION_TO_NONE_LOYALTY,
        changeType,
        description: `${INTERNAL_EFFECTS.DEMOTION_TO_NONE_LOYALTY} loyalty from demotion`,
      }
    }

    case 'lateral':
      return {
        loyaltyChange: INTERNAL_EFFECTS.LATERAL_MOVE_LOYALTY,
        changeType,
        description: `+${INTERNAL_EFFECTS.LATERAL_MOVE_LOYALTY} loyalty from role change`,
      }
  }
}

/**
 * Get loyalty status label and color based on loyalty value.
 */
export function getLoyaltyStatus(loyalty: number): {
  label: string
  colorClass: string
  bgClass: string
  textClass: string
} {
  if (loyalty < INTERNAL_EFFECTS.LOYALTY_CRITICAL) {
    return {
      label: 'Critical',
      colorClass: 'text-red-600 dark:text-red-400',
      bgClass: 'bg-red-100 dark:bg-red-900/30',
      textClass: 'text-red-700 dark:text-red-300',
    }
  }
  if (loyalty < INTERNAL_EFFECTS.LOYALTY_LOW) {
    return {
      label: 'Low',
      colorClass: 'text-orange-600 dark:text-orange-400',
      bgClass: 'bg-orange-100 dark:bg-orange-900/30',
      textClass: 'text-orange-700 dark:text-orange-300',
    }
  }
  if (loyalty < INTERNAL_EFFECTS.LOYALTY_MODERATE) {
    return {
      label: 'Moderate',
      colorClass: 'text-yellow-600 dark:text-yellow-400',
      bgClass: 'bg-yellow-100 dark:bg-yellow-900/30',
      textClass: 'text-yellow-700 dark:text-yellow-300',
    }
  }
  return {
    label: 'Healthy',
    colorClass: 'text-green-600 dark:text-green-400',
    bgClass: 'bg-green-100 dark:bg-green-900/30',
    textClass: 'text-green-700 dark:text-green-300',
  }
}

/**
 * Check if a candidate should show a warning indicator.
 */
export function shouldShowLoyaltyWarning(loyalty: number): boolean {
  return loyalty < INTERNAL_EFFECTS.LOYALTY_WARNING_THRESHOLD
}
